import React, { useEffect } from 'react';
import { ConfigProvider, App } from 'antd';
import { useSelector } from 'react-redux';
import { AppState } from '../../app.state';
import colors from '../colors';
import { get } from 'lodash';

export const useAppConfig = () => {
  const app = useSelector((state: AppState) => state.app);
  const antd = App.useApp();

  const obj = {
    ...antd,
    ...(app || {}),
    style: {
      ...get(app, 'style', {}),
      colors: {
        ...colors,
        ...get(app, 'style.colors', {}),
      },
    },
  };

  const getColor = (str: string) => {
    return get(obj, `style.colors.${str}`);
  };

  return {
    ...obj,
    getColor,
  };
};

export type AppConfig = ReturnType<typeof useAppConfig>;

export const AppConfigProvider = (props: any) => {
  const app = useAppConfig();

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: app.getColor('primary'),
          colorLink: app.getColor('primary'),
          colorInfo: app.getColor('primary'),
          borderRadius: 0,
          lineWidth: 2,
          colorBorder: app.getColor('inputBorder'),
          fontSize: 14,
          fontFamily:
            "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', 'Helvetica Neue', Helvetica, Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'",
        },
        components: {
          Form: {
            labelFontSize: 12,
            labelColor: app.getColor('labelColor'),
          },
          Menu: {
            itemColor: 'rgba(0, 0, 0, 0.65)',
            horizontalItemSelectedColor: app.getColor('primary'),
            horizontalLineHeight: '46px',
            itemHeight: 40,
            iconSize: 16,
            fontSize: 14,
            itemMarginInline: 0,
            itemMarginBlock: 0,
            itemBorderRadius: 0,
          },
          Table: {
            headerSortActiveBg: 'transparent',
            headerSortHoverBg: 'transparent',
            bodySortBg: 'transparent',
            rowHoverBg: 'rgba(111, 75, 158, 0.18)',
          },
          // AER-1648: AntD6 gives Select/Input their own component-level tokens
          // for the active/hover border and the popup's selected/active option
          // background, and those default to the framework blue (#1677ff) rather
          // than inheriting the global colorPrimary purple. On Add Model the raw
          // <Select>/<TextArea>/<Input> controls therefore rendered with a blue
          // focus border and a blue selected dropdown option, unlike the rest of
          // the app. Pin every relevant state to the brand purple (and the same
          // rgba(111,75,158,0.18) tint used for Table.rowHoverBg) so all forms,
          // including the Add Service Record reference screen, stay grey/purple
          // and square (borderRadius: 0).
          Select: {
            borderRadius: 0,
            activeBorderColor: app.getColor('primary'),
            hoverBorderColor: app.getColor('primary'),
            activeOutlineColor: 'rgba(111, 75, 158, 0.18)',
            optionSelectedBg: 'rgba(111, 75, 158, 0.18)',
            optionActiveBg: 'rgba(111, 75, 158, 0.18)',
            optionSelectedColor: app.getColor('labelColor'),
          },
          Input: {
            borderRadius: 0,
            activeBorderColor: app.getColor('primary'),
            hoverBorderColor: app.getColor('primary'),
            activeShadow: 'none',
          },
        },
      }}
    >
      <App>
        {props.children}
      </App>
    </ConfigProvider>
  );
};

export const WithAppConfig = (Comp: any) => {
  return (props: any) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const app = useAppConfig();    

    return <Comp app={app} {...props} />;
  };
};
